import { size } from 'underscore';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Switch } from '@fluentui/react-components';
import { BidAdjustment } from '@bingads-webui-campaign/target-bid-adjustment';
import { AgeRange } from '@bingads-webui-campaign-react/xandr-campaign';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getBidAdjustmentStylesTargetStyles } from './styles/bid-adjustments-styles';

export const AgeRanges = {
  EighteenToTwentyFive: '18-24',
  TwentyFiveToThirtyFive: '25-34',
  ThirtyFiveToFifty: '35-49',
  FiftyToSixtyFive: '50-64',
  SixtyFiveAndAbove: '65+',
};

export const AdGroupAgeRanges = {
  ...AgeRanges,
  Unknown: 'Unknown',
};

const getTarget = (target, type) => {
  switch (type) {
    case 'Device':
      return target && target.DeviceCriterion && target.DeviceCriterion.DeviceName;
    case 'Gender':
      return target && target.GenderCriterion && target.GenderCriterion.Target;
    case 'Age':
      return target && target.AgeCriterion && AdGroupAgeRanges[target.AgeCriterion.Target];
    case 'CompanyName':
      return target && target.CompanyNameCriterion && target.CompanyNameCriterion.Target;
    case 'Industry':
      return target && target.IndustryCriterion && target.IndustryCriterion.Target;
    case 'JobFunction':
      return target && target.JobFunctionCriterion && target.JobFunctionCriterion.Target;
    default:
      return '';
  }
};

const localizedCriterionType = (i18n, criterionType) => {
  switch (criterionType) {
    case 'Device':
      return i18n.getString(_TL_('Device'));
    case 'Gender':
      return i18n.getString(_TL_('Gender'));
    case 'Age':
      return i18n.getString(_TL_('Age'));
    case 'CompanyName':
      return i18n.getString(_TL_('Company name'));
    case 'Industry':
      return i18n.getString(_TL_('Industry'));
    case 'JobFunction':
      return i18n.getString(_TL_('Job function'));
    default:
      return '';
  }
};

export const getBidAdjuestmentTitleDetails = (i18n, params = [], type, useCampaignSettings = false) => {
  let details = '';
  const len = params.length;
  params.forEach((key, index) => {
    const { BidAdjustment: bid } = key && key.Criterion;
    details += `${i18n.getString(_TL_('Targeted'))}: ${i18n.getString(_TL_(getTarget(key && key.Criterion, type)))}, `;
    details += `${bid < 0 ? i18n.getString(_TL_('Decrease by')) : i18n.getString(_TL_('Increase by'))}: ${Math.abs(bid)}%`;
    if (details && index < len - 1) {
      details += ', ';
    }
  });
  return useCampaignSettings ? i18n.getString(_TL_('Using campaign settings: {{settings}}'), { settings: details }) : details;
};

export const getBidAdjuestmentTitleSummary = (i18n, targets, strings, useCampaignSettings = false) => {
  const {
    noTarget = _TL_('No targets set'),
    singleTarget = '',
    multipleTargets = '',
  } = strings;
  const number = size(targets);
  let formattedTargets = '';
  switch (number) {
    case 0:
      formattedTargets = i18n.getString(noTarget);
      break;
    case 1:
      formattedTargets = i18n.getString(singleTarget, { number });
      break;
    default:
      formattedTargets = i18n.getString(multipleTargets, { number });
      break;
  }
  return useCampaignSettings ? i18n.getString(_TL_('Using campaign settings: {{settings}}'), { settings: formattedTargets }) : formattedTargets;
};

const BidAdjustmentInternal = ({
  cid,
  aid,
  campaignId,
  adGroupId,
  criterionType,
  criterionLevel,
  level,
  updateValidation,
  i18n,
  data,
  ranges,
  readOnly,
  parentData,
}) => {
  const [bidAdjustmentRef, setBidAdjustmentRef] = useState();
  const [checked, setChecked] = useState(readOnly);

  const classes = useDefaultStyles(getBidAdjustmentStylesTargetStyles);

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: criterionLevel,
      isForVNext: true,
      initAudienceAssociationView: false,
    })), [criterionLevel]);

  const currentParentLevelTargetInfo = useMemo(() => new AudienceTargetInfo({
    resourceName: 'CampaignCriterion',
    isForVNext: true,
    initAudienceAssociationView: false,
  }), []);

  const onSwitchChange = useCallback((e) => {
    setChecked(e.currentTarget.checked);
    const updatedValue = { [`currentSelected${criterionType}Level`]: e.currentTarget.checked ? 'campaign' : 'adgroup' };

    updateValidation([], updatedValue);
  }, [criterionType, updateValidation]);

  const onChange = useCallback((valid = true, newValue) => {
    const updatedValue = { [criterionType]: newValue || currentLevelTargetInfo.getSnapshot(false) };

    updateValidation(valid ? [] : [i18n.getString(_TL_('{{criterionType}} target has validation errors'), { criterionType: localizedCriterionType(i18n, criterionType) })], updatedValue);
  }, [criterionType, currentLevelTargetInfo, i18n, updateValidation]);

  const bidAdjustmentGridview = useMemo(() => {
    const view = new BidAdjustment({
      currentLevelTargetInfo: readOnly ? currentParentLevelTargetInfo : currentLevelTargetInfo,
      isForVNext: true,
      campaignId,
      adGroupId,
      criterionTypes: [criterionType],
      supportCashbackSettings: false,
      showExclusion: false,
      isAudienceCampaign: false,
      level,
      onChange,
      readonly: readOnly,
    });

    view.render();

    return view;
  }, [adGroupId, campaignId, criterionType, currentLevelTargetInfo, currentParentLevelTargetInfo, level, onChange, readOnly]);

  useEffect(() => {
    if (bidAdjustmentRef && bidAdjustmentGridview) {
      bidAdjustmentRef.innerHTML = '';
      bidAdjustmentRef.append(bidAdjustmentGridview.el);
    }
  }, [bidAdjustmentRef, bidAdjustmentGridview]);

  useEffect(() => {
    if (bidAdjustmentRef) {
      currentLevelTargetInfo.off(`${criterionLevel}${criterionType}Changed`, () => onChange());
      if (data && data.length > 0) {
        currentLevelTargetInfo.destroyAllAsync();
        currentLevelTargetInfo.createMultipleAsync(data);
      }
      if (parentData && parentData.length > 0) {
        currentParentLevelTargetInfo.destroyAllAsync();
        currentParentLevelTargetInfo.createMultipleAsync(parentData);
      }

      currentLevelTargetInfo.on(`${criterionLevel}${criterionType}Changed`, () => onChange());
    }
  }, [aid, bidAdjustmentRef, campaignId, adGroupId, cid, onChange, criterionType, criterionLevel, level, readOnly, ranges]);

  return (
    <div>
      {level === 'adgroup' && <Switch
        checked={checked}
        onChange={onSwitchChange}
        label={i18n.getString(_TL_('Use campaign settings'))}
      />
      }
      {level === 'adgroup' && !readOnly && criterionType === 'Age' && (
        <AgeRange
          i18n={i18n}
          ageRanges={ranges}
        />
      )}
      <div className={classNames('bid-adjustment-container', classes.pickerContainer)} >
        <div className="criterion-setting-container">
          <div className="target-edit-container">
            <div className="criterion-setting" ref={newRef => setBidAdjustmentRef(newRef)} />
          </div>
        </div>
      </div>
    </div>
  );
};

BidAdjustmentInternal.propTypes = {
  criterionType: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  adGroupId: PropTypes.string.isRequired,
  criterionLevel: PropTypes.string,
  updateValidation: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  level: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ranges: PropTypes.arrayOf(PropTypes.string),
  readOnly: PropTypes.bool,
  parentData: PropTypes.arrayOf(PropTypes.shape({})),
};

BidAdjustmentInternal.defaultProps = {
  criterionLevel: 'CampaignCriterion',
  level: 'campaign',
  readOnly: false,
  parentData: null,
  ranges: undefined,
};

export const ValidatedBidAdjustment = withCustomValidation(BidAdjustmentInternal);
